/**
 * ZET-5048: plain-language copy for the internal pre-ping suppression fields.
 *
 * The field names describe the data model rather than the business rule, which is where the
 * confusion comes from: "Level" says nothing about whether the limit counts records or adds up
 * dollars, and "Amount"/"Type" together are a lookback window rather than anything to do with the
 * cap. These strings say what each control actually does to a user's experience.
 */

/**
 * Suppression levels whose Max Limit is a dollar amount rather than a number of times.
 *
 * Calculated Revenue is deliberately NOT here. The name reads like money, but it is only offered
 * on offers, and the offer path counts impressions that generated any revenue
 * (UserImpressionRepository.getTotalImpressionsForOfferByCalculatedRevenueNative is
 * `COUNT(*) ... WHERE ui.calculated_revenue > 0`). Its Max Limit is therefore a number of
 * revenue-generating impressions. Revenue Generated, on offer actions, really does sum dollars.
 */
const REVENUE_LEVELS = new Set(['revenue_generated', 'email_redirect_rev_generated']);

/** True when Max Limit should read as money for the given level. */
export const isRevenueSuppressionLevel = (level?: string | null): boolean =>
  Boolean(level && REVENUE_LEVELS.has(level));

export const SUPPRESSION_TOOLTIPS = {
  type: 'The unit of the lookback window — how far back we look for this user’s history. Together with Amount: "Days" + "7" means "anything in the last 7 days". Nothing outside the window counts toward the limit.',

  amount: 'How many of the chosen unit the lookback window covers. "7" with Type "Days" looks back one week. Older history is ignored, so a user suppressed today can become eligible again once their history ages out of the window.',

  level: 'What we count during the lookback window:\n\n• Impression / Action Taken — how many times this was shown or fired.\n• Accepted — how many times the user accepted it.\n• Answer — how many times the user gave the selected answer.\n• Calculated Revenue — how many times the user saw this and it earned something. A count of occasions, not an amount.\n• Revenue Generated — how much money this earned from the user, in dollars.\n• Email Redirect — how many times we sent the user to this advertiser from an email link.\n• Email Redirect Rev Generated — how much money those email hand-offs earned, in dollars.\n\nRevenue Generated and Email Redirect Rev Generated add up dollars; every other level counts occurrences, Calculated Revenue included. That is what Max Limit is compared against.',

  maxLimitCount:
    'The ceiling, counted over the lookback window. Suppression starts once the user is already past it, so a Max Limit of 3 lets a 4th through and stops the 5th. Leave it at 0, or empty, to suppress on any history at all — one occurrence is enough to stop it.',

  maxLimitRevenue:
    'The ceiling in dollars, totalled over the lookback window. Suppression starts once the user is already past it, so a Max Limit of $3 still serves at exactly $3.00 and stops once they go beyond it. Only revenue already recorded counts — we do not predict what the next hand-off would earn, so the one that carries them past the ceiling still goes through and the one after it is suppressed. Leave it at 0, or empty, to suppress as soon as the user has generated any revenue at all.',
} as const;

/**
 * Placeholder for Max Limit. A revenue level needs it spelled out: "0" reads as "no cap" to
 * most people, when it means the opposite - any revenue at all suppresses.
 */
export const maxLimitPlaceholder = (level?: string | null): string =>
  isRevenueSuppressionLevel(level)
    ? 'Leave as 0 or empty to suppress as soon as the user has generated any revenue at all'
    : 'Leave as 0 or empty to suppress as soon as the user has any history at all';

/** Max Limit means something different per level, so the tooltip follows the selection. */
export const maxLimitTooltip = (level?: string | null): string =>
  isRevenueSuppressionLevel(level) ? SUPPRESSION_TOOLTIPS.maxLimitRevenue : SUPPRESSION_TOOLTIPS.maxLimitCount;
